


































import {Component, Watch} from 'vue-property-decorator';
import {DateTime} from 'luxon';
import {
    BOOKING_HOURS,
    BOOKING_STATUS, BookingUIConfig,
    CALENDAR_FORMAT,
    CLAIM_STATUS,
    getStoreSettings,
    nil,
    ORDER_STATUS,
    ORDER_STATUS_METADATA
} from '@/common';
import {FeathersVuexFindAll, getZonedBookingDate} from '@/utils';
import BookingDateDialog from '@/components/Views/Dashboard/Booking/BookingDateDialog.vue';
import BookingCalendarBadge from '@/components/Views/Dashboard/Booking/BookingCalendarBadge.vue';
import {flatten, debounce} from 'lodash';
import {Mixins} from 'vue-property-decorator';
import UserRoleMixin from '@/mixins/UserRoleMixin.vue';

@Component({
    components: {BookingCalendarBadge, BookingDateDialog},
})
export default class BookingCalendarView extends Mixins(UserRoleMixin) {
    public get startDate() {
        return this.currentTime.toFormat(`yyyy-MM-${this.dayMode ? 'dd' : '01'}`);
    }

    public get startDateAsDateTime() {
        return DateTime.fromFormat(this.startDate, 'yyyy-MM-dd');
    }
    private orderStatusMetadata = ORDER_STATUS_METADATA;
    private enabledOrderStatuses = {};

    public loading = true;
    public viewingDay = false;
    public pickedDate = DateTime.local().toFormat(`yyyy-MM${this.dayMode ? '-dd' : ''}`);

    public validHours = [];
    public datePickerOpen = false;
    public currentTime = DateTime.local();
    public bookings: any[] = [];
    public bookingsKeyedByStatusOrders = [];
    public assignedVendorBookings: any[] = [];

    public get assignedVendorBookingsMap(): any[] {
        return this.asDateMap(this.assignedVendorBookings);
    }

    public vendorRejectedBookings: any[] = [];

    public get vendorRejectedBookingsMap(): any[] {
        return this.asDateMap(this.vendorRejectedBookings);
    }

    public vendorCancelledBookings: any[] = [];

    public get vendorCancelledBookingsMap(): any[] {
        return this.asDateMap(this.vendorCancelledBookings);
    }

    public requestedVendorBookings: any[] = [];

    public get requestedVendorBookingsMap(): any[] {
        return this.asDateMap(this.requestedVendorBookings);
    }

    public vendorAvailableBookings: any[] = [];

    public get vendorAvailableBookingsMap(): any[] {
        return this.asDateMap(this.vendorAvailableBookings);
    }

    public bookingsInDialog: any = null;

    public get bookingsInDialogMap(): any[] {
        return this.asDateMap(this.bookingsInDialog);
    }

    public get calendarStyle() {
        return `${this.dayMode ? '' : `min-height:${this.calendarHeight}`}px`;
    }

    public get calendarHeight() {
        return 750;
    }

    public mounted() {
        for(const os of ORDER_STATUS_METADATA){
            if(this.userIsAdmin) {
                this.enabledOrderStatuses[os.status] = os.displayedByDefault;
            }
        }
        this.refreshBookings();
    }
    public get firstHour() {
        return this.validHours[0];
    }
    public get lastHour() {
        return this.validHours[this.validHours.length-1];
    }
    private refreshBookingsDebounced = debounce(() => {
        this.refreshBookings()
    }, 850);

    @Watch('pickedDate')
    public onPropertyChanged(value: string, oldValue: string) {
        this.currentTime = DateTime.fromFormat(value, `yyyy-MM${this.dayMode ? '-dd' : ''}`);
    }

    @Watch('currentTime')
    public onCurrentDateChanged(value: DateTime, oldValue: DateTime) {
        this.refreshBookings();
    }

    public asDateMap(entries: any[]) {
        const map: any = [];
        entries.forEach((e) => (map[getZonedBookingDate(e).toFormat(CALENDAR_FORMAT)] = map[getZonedBookingDate(e).toFormat(CALENDAR_FORMAT)] || []).push(e));
        return map;
    }

    public orderStatusMetadataOf(status): BookingUIConfig{
        for(const o of this.orderStatusMetadata){
            if(o.status === status) return o;
        }
        return {
            dark: false,
            icon: "",
            status: "",
            title: "",
            color: 'info',
            displayedByDefault: false,
            label:''
        };
    }

    public moveCalendar(forward: boolean = true) {
        const amount = this.dayMode ? {days: forward ? 1 : -1} : {months: forward ? 1 : -1};
        this.pickedDate = this.currentTime.plus(amount).toFormat(`yyyy-MM${this.dayMode ? '-dd' : ''}`);
    }

    public onDayClicked(day: any) {
        this.bookingsInDialog = this.getBookingsForDay(day.date);
        if (flatten(this.bookingsInDialog.map((x) => x.content)).length === 0) {
            return;
        }
        (this.$refs.viewBookingsDialog as any).open = true;
    }

    public onIntervalClicked(interval: any) {
        this.bookingsInDialog = this.getBookingsForDay(interval.date, interval.hour);
        if (flatten(this.bookingsInDialog.map((x) => x.content)).length === 0) {
            return;
        }
        (this.$refs.viewBookingsDialog as any).open = true;
    }

    public functionEvents(day: any) {
        return this.getBookingsForDay(day).filter((x) => x.content.length > 0).map((x) => x.color);
    }

    public eventsForHour(hour: number) {
        const bookings = this.getBookingsForDay(this.pickedDate).filter((x) => x.content.length > 0);
        return false;
    }

    public getBookingsForDay(day: string, filterHour: number | null = null) {
        let bookingsForDay: any[] = [];
        if (this.userIsVendor) {
            bookingsForDay = [
                {
                    icon: 'fas fa-calendar-check',
                    color: 'success',
                    text: 'Accepted',
                    title: 'Your Bookings For This Day',
                    content: this.assignedVendorBookingsMap[day] || [],
                },
                {
                    icon: 'fas fa-hands',
                    text: 'Requested',
                    color: 'info',
                    title: 'Bookings You\'ve Been Requested For',
                    content: this.requestedVendorBookingsMap[day] || [],
                },
                {
                    icon: 'fas fa-calendar-times',
                    color: 'error',
                    text: 'Rejected',
                    title: 'Rejected Bookings',
                    content: this.vendorRejectedBookingsMap[day] || [],
                },
                {
                    icon: 'fas fa-calendar-times',
                    color: 'error',
                    text: 'Cancelled',
                    title: 'Cancelled Bookings',
                    content: this.vendorCancelledBookingsMap[day] || [],
                },
                {
                    icon: 'fas fa-hand-holding-usd',
                    color: 'success',
                    text: 'Available',
                    dark: true,
                    title: 'Available Bookings',
                    content: this.vendorAvailableBookingsMap[day] || [],
                },
            ];
        } else {
            let accepted = [];
            for(const osm of ORDER_STATUS_METADATA){
                if(nil(this.bookingsKeyedByStatusOrders[osm.status],true)){
                   continue;
                }
                const adm = this.asDateMap(this.bookingsKeyedByStatusOrders[osm.status] || []);
                if(nil(adm[day], true)) continue;
                accepted = adm[day].filter(x=>!nil(x.assignedVendor));
                adm[day] = adm[day].filter(x=>nil(x.assignedVendor));
                bookingsForDay.push({
                    icon: osm.icon,
                    color: osm.color,
                    dark: osm.dark,
                    text: osm.label,
                    title: osm.title,
                    content: adm[day] || [],
                });
                if(accepted.length > 0){
                    bookingsForDay.push({
                        icon: 'fas fa-calendar-check',
                        color: 'success',
                        dark: true,
                        text: 'Pro Accepted',
                        title: 'Accepted By Pro',
                        content: accepted,
                    });
                }
            }

        }
        if (!nil(filterHour)) {
            for (const b of bookingsForDay) {
                if (b.content.length > 0) {
                    b.content = b.content.filter((x) => {
                        const h = DateTime.fromISO(x.localizedDate, {setZone: true}).hour;
                        return h === filterHour || (h > filterHour && h + x.duration <= filterHour);
                    });
                }
            }
        }
        return bookingsForDay;
    }

    public async refreshBookings() {
        this.loading = true;
        const {Booking, VendorClaim, Setting} = this.$FeathersVuex;
        const {roles, vendorModel} = this.$store.state.auth.user;
        const date = {
            $gte: this.startDateAsDateTime.toISODate(),
            $lte: this.startDateAsDateTime.plus({months: 1}).toISODate(),
        };
        this.validHours = BOOKING_HOURS.getHours(await getStoreSettings(Setting));
        const vendorSpecificBookingQuery = this.userIsVendor ? {
            $or: [
                // available for any
                {
                    status: BOOKING_STATUS.WAITING_ON_ANY_PRO,
                },
                // orders I have accepted
                {
                    assignedVendor: vendorModel._id,
                },
                // orders I was requested in
                {
                    requestedVendor: vendorModel._id,
                },
            ],
        } : {};
        let allBookings = await FeathersVuexFindAll(Booking, {
            query: {
                ...vendorSpecificBookingQuery,
                date,
            },
        }, true);
        if(this.userIsAdmin) {
            allBookings = allBookings.filter(x => this.enabledOrderStatuses[x.orderStatus]);
        } else {
            allBookings = allBookings.filter(x => [ORDER_STATUS.PAID, ORDER_STATUS.FULFILLED, ORDER_STATUS.ACCEPTED, ORDER_STATUS.CANCELLED].indexOf(x.orderStatus) > -1)
        }
        const images = await this.$FeathersVuex.KeyedImage.find({query: {
                key: {$in: ['Hair Thumbnail', 'Makeup Thumbnail']},
            }});
        const vendorSpecificClaimQuery = this.userIsVendor ? {vendor: vendorModel._id} : {};
        const bookingIds = allBookings.map((x) => x._id);
        const claims = await FeathersVuexFindAll(VendorClaim, {
            query: {
                ...vendorSpecificClaimQuery,
                booking: {
                    $in: bookingIds,
                },
            },
        }, true);
        this.vendorAvailableBookings = [];
        this.requestedVendorBookings = [];
        this.assignedVendorBookings = [];
        this.vendorRejectedBookings = [];
        this.vendorCancelledBookings = [];

        if (this.userIsVendor) {
            for (const booking of allBookings) {
                let requested = false;
                if (booking.requestedVendor === vendorModel._id) {
                    requested = true;
                }
                const [claim] = claims.filter((x) => x.booking === booking._id);
                if (!nil(claim, true)) {
                    if(booking.status === BOOKING_STATUS.CANCELLED){
                        this.vendorCancelledBookings.push(booking);
                        continue;
                    }
                    switch (claim.status) {
                        case CLAIM_STATUS.UNKNOWN:
                            requested ? this.requestedVendorBookings.push(booking) : this.vendorAvailableBookings.push(booking);
                            break;
                        case CLAIM_STATUS.REJECTED:
                            this.vendorRejectedBookings.push(booking);
                            break;
                        case CLAIM_STATUS.ACCEPTED:
                        case CLAIM_STATUS.ASSIGNED:
                            this.assignedVendorBookings.push(booking);
                            break;
                    }
                } else {
                    // this.vendorAvailableBookings.push(booking);
                }
            }
        } else {
            for(const osm of ORDER_STATUS_METADATA){
                this.bookingsKeyedByStatusOrders[osm.status] = [];
            }
            for(const booking of allBookings) {
                this.bookingsKeyedByStatusOrders[booking.orderStatus].push(booking);
            }
        }
        this.loading = false;
    }

    public get dayMode() {
        return this.isXS(null);
    }


    /*public get endDate(){

        luxon.DateTime.fromFormat("2019-05-01","yyyy-MM-dd")
    }*/
}
